'use client';

import React from 'react';
import Image from 'next/image';
import { cn } from '@utils';
import { tv } from 'tailwind-variants';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
	variant?: 'default' | 'outlined' | 'elevated';
	theme?: 'light' | 'dark';
	actionType?: 'single' | 'multiple' | 'none'; // check the QGDS as the guidlines
	image?: string;
	imageAlt?: string;
	contentContainerClassName?: string;
}

//TODO it still need to be adjsuted for "multiple" actionType and also wide single actionType, based on the QGDS
const card = tv({
	base: ['border-light-border-alt rounded-sm border'],
	variants: {
		variant: {
			default: '',
			outlined: '',
			elevated: '',
		},
		theme: {
			light: '',
			dark: '',
		},
		actionType: {
			single: ['group hover:cursor-pointer'],
			multiple: '',
			none: '',
		},
		isDisabled: {
			true: 'cursor-not-allowed opacity-50',
		},
	},
	compoundVariants: [
		{
			variant: 'default',
			theme: 'light',
			class: 'bg-neutral-white text-light-text',
		},
		{
			variant: 'default',
			theme: 'dark',
			class: 'bg-dark-background border-dark-border text-dark-text',
		},
		{
			variant: 'outlined',
			theme: 'light',
			class: 'bg-neutral-white text-light-text',
		},
		{
			variant: 'outlined',
			theme: 'dark',
			class: 'bg-dark-background border-dark-border text-dark-text',
		},
		{
			variant: 'elevated',
			theme: 'light',
			class: 'bg-neutral-white text-light-text',
		},
		{
			variant: 'elevated',
			theme: 'dark',
			class: 'bg-dark-background text-dark-text',
		},
	],
	defaultVariants: {
		variant: 'default',
		theme: 'light',
		actionType: 'none',
	},
});

const Card = ({
	variant,
	theme,
	actionType,
	image,
	imageAlt = '',
	children,
	className,
	contentContainerClassName,
	...props
}: CardProps) => {
	return (
		<div
			data-slot="card"
			className={card({
				variant,
				theme,
				actionType,
				className,
			})}
			{...props}
		>
			{image && (
				<div className="relative aspect-[16/9] w-full overflow-hidden rounded-sm">
					<Image src={image} alt={imageAlt} fill className="object-cover" />
				</div>
			)}
			<div className={cn('flex flex-col gap-3 p-4', contentContainerClassName)}>{children}</div>
		</div>
	);
};

const CardHeader = ({
	className,
	children,
	...props
}: React.HTMLAttributes<HTMLHeadingElement>) => {
	return (
		<div data-slot="card-header" className={cn('flex flex-col gap-1', className)} {...props}>
			{children}
		</div>
	);
};

const CardHeadline = ({
	className,
	children,
	...props
}: React.HTMLAttributes<HTMLHeadingElement>) => {
	return (
		<h3
			data-slot="card-headline"
			className={cn('text-balance group-hover:underline', className)}
			{...props}
		>
			{children}
		</h3>
	);
};

const CardDescriptor = ({
	className,
	children,
	...props
}: React.HTMLAttributes<HTMLParagraphElement>) => (
	<p data-slot="card-descriptor" className={className} {...props}>
		{children}
	</p>
);

const CardInner = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div data-slot="card-inner" className={cn('flex flex-col gap-1 text-sm', className)} {...props}>
		{children}
	</div>
);

const CardFooter = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div data-slot="card-footer" className={cn('flex flex-col gap-1 text-sm', className)} {...props}>
		{children}
	</div>
);

Card.Header = CardHeader;
Card.Headline = CardHeadline;
Card.Descriptor = CardDescriptor;
Card.Inner = CardInner;
Card.Footer = CardFooter;

export { Card };
